import { XMarkIcon, DocumentDuplicateIcon, ForwardIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import type { BulkRunState } from "./hooks/useBulkRun";
import type { MigrationRowData, TargetSummary } from "./utils/constants";

interface Props {
  open: boolean;
  rows: MigrationRowData[];
  targets: TargetSummary[];
  state: BulkRunState;
  onClose: () => void;
  onCopyReport: () => void;
}

type CellStatus = "queued" | "running" | "success" | "skipped" | "failed";

function getCellStatus(
  state: BulkRunState,
  migrationId: number,
  targetId: string,
): CellStatus {
  // Walk events in reverse to find the latest relevant event for this cell
  let latestSuccess = false;
  let latestSkipped = false;
  let latestFailure = false;
  let latestBegin = false;

  for (let i = state.events.length - 1; i >= 0; i--) {
    const ev = state.events[i];
    if (ev.migrationId !== migrationId || ev.target !== targetId) continue;
    if (ev.type === "success" && !latestFailure) { latestSuccess = true; break; }
    if (ev.type === "skipped" && !latestFailure) { latestSkipped = true; break; }
    if (ev.type === "failure" && !latestSuccess && !latestSkipped) { latestFailure = true; break; }
    if (ev.type === "begin" && !latestSuccess && !latestSkipped && !latestFailure) { latestBegin = true; break; }
  }

  if (latestSuccess) return "success";
  if (latestSkipped) return "skipped";
  if (latestFailure) return "failed";
  if (latestBegin) return "running";
  return "queued";
}

function CellIndicator({ status }: { status: CellStatus }) {
  if (status === "success") {
    return (
      <div className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center">
        <CheckIcon className="w-3.5 h-3.5 text-emerald-600" />
      </div>
    );
  }
  if (status === "skipped") {
    return (
      <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center" title="Already applied — skipped">
        <ForwardIcon className="w-3.5 h-3.5 text-slate-500" />
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center">
        <XMarkIcon className="w-3.5 h-3.5 text-red-600" />
      </div>
    );
  }
  if (status === "running") {
    return (
      <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center">
        <span className="w-2.5 h-2.5 rounded-full bg-blue-600 animate-pulse" />
      </div>
    );
  }
  // queued
  return (
    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
      <span className="w-2 h-2 rounded-full bg-slate-400" />
    </div>
  );
}

function statusLabel(s: BulkRunState["status"]): string {
  switch (s) {
    case "running": return "Running…";
    case "complete": return "Complete";
    case "aborted": return "Aborted";
    case "failed": return "Failed";
    default: return "Idle";
  }
}

function statusDotColor(s: BulkRunState["status"]): string {
  switch (s) {
    case "running": return "bg-blue-600 animate-pulse";
    case "complete": return "bg-emerald-500";
    case "aborted": return "bg-amber-500";
    case "failed": return "bg-red-500";
    default: return "bg-slate-400";
  }
}

export default function BulkRunDrawer({ open, rows, targets, state, onClose, onCopyReport }: Props) {
  if (!open) return null;

  const failedCount = state.events.filter((e) => e.type === "failure").length;
  const isDone = state.status === "complete" || state.status === "aborted" || state.status === "failed";

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 flex flex-col w-full max-w-2xl bg-white border-l border-slate-200 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 shrink-0">
          <div className="flex items-center gap-3">
            <span className={`w-2.5 h-2.5 rounded-full ${statusDotColor(state.status)}`} />
            <div>
              <h2 className="text-base font-bold text-slate-900">Bulk Migration Run</h2>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {statusLabel(state.status)}
                {state.totalOperations > 0 && (
                  <> · {state.completedOperations} of {state.totalOperations} complete</>
                )}
                {failedCount > 0 && (
                  <span className="text-red-600"> · {failedCount} failed</span>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Matrix */}
        <div className="flex-1 overflow-auto p-6">
          {rows.length === 0 || targets.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-12">No operations to display.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr>
                    <th className="py-2 pr-4 text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap min-w-[200px]">
                      Migration
                    </th>
                    {targets.map((t) => (
                      <th
                        key={t.id}
                        className="py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 text-center whitespace-nowrap"
                      >
                        {t.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50">
                      <td className="py-2.5 pr-4 align-middle">
                        <div className="text-[11px] font-mono text-slate-700 truncate max-w-[240px]">
                          <span className="text-slate-400">{row.folderName}/</span>
                          {row.fileName}
                        </div>
                        <div className="text-[10px] text-blue-600 font-bold">v{row.version}</div>
                      </td>
                      {targets.map((t) => {
                        const status = getCellStatus(state, row.id, t.id);
                        return (
                          <td key={t.id} className="py-2.5 px-3 align-middle">
                            <div className="flex justify-center">
                              <CellIndicator status={status} />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-slate-200 px-6 py-4 bg-slate-50 flex items-center justify-between gap-4">
          <div className="text-[12px] text-slate-700">
            {state.totalOperations > 0
              ? (
                <>
                  <span className="text-slate-900 font-semibold">{state.completedOperations}</span> of{" "}
                  <span className="text-slate-900 font-semibold">{state.totalOperations}</span> complete
                  {failedCount > 0 && (
                    <> · <span className="text-red-600 font-semibold">{failedCount} failed</span></>
                  )}
                </>
              )
              : state.status === "running"
                ? "Waiting for first event…"
                : "No operations recorded."}
          </div>
          <div className="flex items-center gap-2">
            {isDone && (
              <button
                type="button"
                onClick={onCopyReport}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[12px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
              >
                <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                Copy report
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-2 text-sm font-medium bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
